import { PropsRecomendacaoPublicacao } from '@/components/molecules/RecomendacaoPublicacao';
import { PaginaPublicacaoProps } from '@/components/templates/PaginaPublicacaoTemplate';
import { useScrollAteTopo } from '@/hooks/useScrollAteTopo';
import { useSnackbar } from '@/hooks/useSnackbar';
import {
  recuperarCriticaPorSlug,
  recuperarCriticasRecomendadas,
} from '@/services/conteudoService';
import { Critica } from '@/types/critica';
import converterDataParaFormatoISO from '@/utils/converterDataParaFormatoISO';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

function useCriticasPaginaHook(props: PaginaPublicacaoProps) {
  const { $publicacaoSlug } = props;
  const caminhoURL = usePathname();
  const [critica, setCritica] = useState<Critica>();
  const [criticasRecomendadas, setCriticaRecomendadas] =
    useState<PropsRecomendacaoPublicacao[]>();
  const [criticaSendoCarregada, setCriticaSendoCarregada] =
    useState<boolean>(false);

  const { mostrarSnackbar } = useSnackbar();

  const { scrollarAteOTopo } = useScrollAteTopo();

  async function recuperarCritica() {
    try {
      setCriticaSendoCarregada(true);
      const retornoCriticaFirebase =
        await recuperarCriticaPorSlug($publicacaoSlug);
      if (!retornoCriticaFirebase)
        throw new Error('Não foi possível recuperar a critica');
      setCritica(retornoCriticaFirebase);

      const criticasRecomendadas = await recuperarCriticasRecomendadas({
        criticaSlug: retornoCriticaFirebase.slug,
        tagsCritica: retornoCriticaFirebase.tags,
      });

      console.log('criticasRecomendadas', criticasRecomendadas);

      setCriticaRecomendadas(criticasRecomendadas);
      scrollarAteOTopo();
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setCriticaSendoCarregada(false);
    }
  }

  useEffect(() => {
    recuperarCritica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jsonLDPaginaCritica = useMemo(() => {
    if (!critica) return null;

    const { titulo, imagemArquivo, dataNoticia, publicadoPor } = critica;
    const dataISO = converterDataParaFormatoISO(dataNoticia);
    return {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      image: [imagemArquivo.urlx1 || '/assets/images/logoCirculo@3x.png'],
      headline: titulo,
      datePublished: dataISO,
      dateModified: dataISO,
      author: [{ name: publicadoPor, '@type': 'Person' }],
    };
  }, [critica]);

  const imagemCartazTituloCritica = useMemo(() => {
    if (!critica || !critica.tituloObj || !critica.tituloObj?.posterArquivo)
      return '';

    const { urlx1, urlx2, urlx3 } = critica.tituloObj?.posterArquivo || {};
    return urlx3 || urlx2 || urlx1;
  }, [critica]);

  return {
    critica,
    criticaSendoCarregada,
    caminhoURL,
    jsonLDPaginaCritica,
    imagemCartazTituloCritica,
    criticasRecomendadas,
  };
}

export type useCriticasPaginaHookTypeReturn = ReturnType<
  typeof useCriticasPaginaHook
>;

export default useCriticasPaginaHook;
